import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BannerImageUpload from "./BannerImageUpload";

const EditProfileDialog = ({ open, onOpenChange, user, onSave }) => {
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && open) {
      setEditData({
        name: user.name || "",
        position: user.position || "",
        preferredFoot: user.preferredFoot || "right",
        playStyle: user.playStyle || "balanced",
        profileImage: user.profileImage || "",
        location: user.location || "",
        city: user.city || "",
        district: user.district || "",
        age: user.age || "",
        weight: user.weight || "",
        height: user.height || "",
        experience: user.experience || "",
        bio: user.bio || "",
        themeColor: user.themeColor || "#10b981",
        bannerColor: user.bannerColor || "",
        bannerImage: user.bannerImage || "",
        stats: user.stats || {},
        technicalCard: user.technicalCard || {}
      });
    }
  }, [user, open]);

  const handleSave = async () => {
    setSaving(true);
    // Integer alanları için boş stringleri null yap
    const cleanedData = {
      ...editData,
      age: editData.age === "" || editData.age === undefined ? null : parseInt(editData.age) || null,
      weight: editData.weight === "" || editData.weight === undefined ? null : parseInt(editData.weight) || null,
      height: editData.height === "" || editData.height === undefined ? null : parseInt(editData.height) || null,
    };
    await onSave(cleanedData);
    setSaving(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Profili Düzenle</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Temel Bilgiler</TabsTrigger>
            <TabsTrigger value="physical">Fiziksel & Konum</TabsTrigger>
            <TabsTrigger value="style">Stil & Tema</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Ad Soyad</Label>
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Pozisyon</Label>
                <Select 
                  value={editData.position} 
                  onValueChange={(val) => setEditData({ ...editData, position: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="goalkeeper">Kaleci</SelectItem>
                    <SelectItem value="defender">Defans</SelectItem>
                    <SelectItem value="midfielder">Orta Saha</SelectItem>
                    <SelectItem value="forward">Forvet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Ayak</Label>
                <Select 
                  value={editData.preferredFoot} 
                  onValueChange={(val) => setEditData({ ...editData, preferredFoot: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="right">Sağ</SelectItem>
                    <SelectItem value="left">Sol</SelectItem>
                    <SelectItem value="both">Çift Ayak</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Oyun Tarzı</Label>
                <Select 
                  value={editData.playStyle} 
                  onValueChange={(val) => setEditData({ ...editData, playStyle: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aggressive">Agresif</SelectItem>
                    <SelectItem value="passer">Pasçı</SelectItem>
                    <SelectItem value="technical">Teknik</SelectItem>
                    <SelectItem value="sprinter">Sprinter</SelectItem>
                    <SelectItem value="balanced">Dengeli</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Biyografi</Label>
              <Textarea
                value={editData.bio || ""}
                onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                placeholder="Kendinden bahset..."
                rows={3}
              />
            </div>

            <div>
              <Label>Profil Resmi URL</Label>
              <Input
                value={editData.profileImage}
                onChange={(e) => setEditData({ ...editData, profileImage: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </TabsContent>

          <TabsContent value="physical" className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>İl</Label>
                <Select 
                  value={editData.city || ""}
                  onValueChange={(val) => setEditData({ ...editData, city: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="İl seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="İstanbul">İstanbul</SelectItem>
                    <SelectItem value="Ankara">Ankara</SelectItem>
                    <SelectItem value="İzmir">İzmir</SelectItem>
                    <SelectItem value="Bursa">Bursa</SelectItem>
                    <SelectItem value="Antalya">Antalya</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>İlçe</Label>
                <Input
                  value={editData.district || ""}
                  onChange={(e) => setEditData({ ...editData, district: e.target.value })}
                  placeholder="İlçe"
                />
              </div>
              <div>
                <Label>Konum</Label>
                <Input
                  value={editData.location || ""}
                  onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                  placeholder="Mahalle/Semt"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Yaş</Label>
                <Input
                  type="number"
                  value={editData.age || ""}
                  onChange={(e) => setEditData({ ...editData, age: parseInt(e.target.value) || "" })}
                  placeholder="25"
                />
              </div>
              <div>
                <Label>Boy (cm)</Label>
                <Input
                  type="number"
                  value={editData.height || ""}
                  onChange={(e) => setEditData({ ...editData, height: parseInt(e.target.value) || "" })}
                  placeholder="175"
                />
              </div>
              <div>
                <Label>Kilo (kg)</Label>
                <Input
                  type="number"
                  value={editData.weight || ""}
                  onChange={(e) => setEditData({ ...editData, weight: parseInt(e.target.value) || "" })}
                  placeholder="70"
                />
              </div>
            </div>

            <div>
              <Label>Deneyim Seviyesi</Label>
              <Select 
                value={editData.experience || ""}
                onValueChange={(val) => setEditData({ ...editData, experience: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seviye seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Beginner">Başlangıç</SelectItem>
                  <SelectItem value="Amateur">Amatör</SelectItem>
                  <SelectItem value="Semi-Pro">Yarı Profesyonel</SelectItem>
                  <SelectItem value="Professional">Profesyonel</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </TabsContent>

          <TabsContent value="style" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Tema Rengi</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="color"
                    value={editData.themeColor || "#10b981"}
                    onChange={(e) => setEditData({ ...editData, themeColor: e.target.value })}
                    className="h-10 w-20"
                  />
                  <Input
                    value={editData.themeColor || "#10b981"}
                    onChange={(e) => setEditData({ ...editData, themeColor: e.target.value })}
                    placeholder="#10b981"
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label>Banner Rengi</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="color"
                    value={editData.bannerColor || "#10b981"}
                    onChange={(e) => setEditData({ ...editData, bannerColor: e.target.value })}
                    className="h-10 w-20"
                  />
                  <Input
                    value={editData.bannerColor || ""}
                    onChange={(e) => setEditData({ ...editData, bannerColor: e.target.value })}
                    placeholder="#10b981"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {/* Banner Fotoğrafı Yükleme */}
            <div>
              <Label>Banner Fotoğrafı</Label>
              <p className="text-xs text-gray-500 mb-2">Fotoğraf yüklerseniz renk yerine fotoğraf gösterilir. Kırpma ve konumlandırma yapabilirsiniz.</p>
              
              <BannerImageUpload
                currentImage={editData.bannerImage}
                bannerColor={editData.bannerColor}
                themeColor={editData.themeColor}
                onImageUpdate={(imageData) => setEditData({ ...editData, bannerImage: imageData })}
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
            İptal
          </Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-emerald-600">
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileDialog;
